import { FC, useRef, useState } from "react";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useCarLeasingApi } from "../../http/carLeasings/api";
import { CarLeasingNoCarDepency, isItemExpired, isItemExpiringSoon } from "../../http/carLeasings/types";
import { getCarStateColor, getStatus } from "../../http/cars/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { downloadFileFromData, formatDateNumeric, isWithinLast48h } from "../../types/utils";
import SvgAdd from "../icons/Add";
import SvgTaskAlt from "../icons/TaskAlt";
import Button from "../ui/Button";
import DateColumnFilter from "../ui/columnFilters/DateColumnFilter";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CustomLink from "../ui/CustomLink";
import ExportButton from "../ui/ExportButton";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import { State } from "../ui/State";
import Table from "../ui/Table";
import CarLeasingHistoryDialog from "./CarLeasingHistoryDialog";
import CarLeasingStats from "./CarLeasingStats";
import ConcludeCarLeasingDialog from "./ConcludeCarLeasingDialog";
import CreateCarLeasingDialog from "./CreateCarLeasingDialog";
import DeleteCarLeasingDialog from "./DeleteCarLeasingDialog";
import EditCarLeasingDialog from "./EditCarLeasingDialog";

const CAR_LEASING_FIELDS = [
  "createdAt",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "car.status",
  "contractDate",
  "expirationDate",
  "leasingCompany.id",
  "leasingCompany.name",
  "versionNumber",
] as const;
type CarLeasingField = (typeof CAR_LEASING_FIELDS)[number];
type CarLeasingSearch = NestedPick<CarLeasingNoCarDepency, CarLeasingField>;

type CarLeasingsProps = {
  notificationsOnly?: boolean;
};

const CarLeasings: FC<CarLeasingsProps> = ({ notificationsOnly }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [itemToDelete, setItemToDelete] = useState<CarLeasingSearch>();
  const [itemToEdit, setItemToEdit] = useState<CarLeasingSearch>();
  const [itemToConclude, setItemToConclude] = useState<CarLeasingSearch>();
  const [showNewItem, setShowNewItem] = useState(false);
  const [itemToShowVersions, setItemToShowVersions] = useState<CarLeasingSearch>();
  const carLeasingApi = useCarLeasingApi();
  const states = getStatus(i18n.t);

  const query: PaginationQueryWithFields<CarLeasingNoCarDepency> = {
    ...filters.state,
    q: debouncedValue,
    fields: CAR_LEASING_FIELDS,
  };

  const result = carLeasingApi.useAdminCarLeasing(query, aborter.current.signal, notificationsOnly);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: carLeasingApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: CarLeasingSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.carLeasings.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    if (userSettings.permissions.carLeasings.update) {
      actions.push({
        title: i18n.t.carLeasings.conclude,
        onClick: () => setItemToConclude(item),
        icon: SvgTaskAlt,
        isDisabled: item.car.status !== "active",
      });
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.carLeasings.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <Header title={i18n.t.carLeasings.plural} />
      <Panel>
        <CarLeasingStats query={query} />
        <Table<CarLeasingSearch>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          buttons={{
            main: userSettings.permissions.carLeasings.insert && (
              <Button buttonProps={{ onClick: () => setShowNewItem(true) }} glyph={SvgAdd} />
            ),
            secondary: (
              <ExportButton.Excel
                onClick={async () => {
                  const r = await carLeasingApi.exportExcel(query, aborter.current.signal);
                  r && downloadFileFromData(r.data, "Car leasings", r?.contentType);
                }}
              />
            ),
          }}
          head={
            <>
              <Table.Th
                title={i18n.t.plateNumbers.singular}
                fieldName="carLeasing.car.plateNumber"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.carVin}
                fieldName="carLeasing.car.vin"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.leasingCompanies.singular}
                fieldName="carLeasing.company"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.contractDate}
                fieldName="carLeasing.contractDate"
                order={orderOptions}
                filters={filterOptions}
                filter={DateColumnFilter}
                formatter={(value) =>
                  typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
                }
              />
              <Table.Th
                title={i18n.t.common.expirationDate}
                fieldName="carLeasing.expirationDate"
                order={orderOptions}
                filters={filterOptions}
                filter={DateColumnFilter}
                formatter={(value) =>
                  typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
                }
              />
              <Table.Th
                title={i18n.t.common.state}
                fieldName="carLeasing.car.state"
                order={orderOptions}
                filters={filterOptions}
                filter={TextColumnFilter}
                formatter={(value) => states.find((x) => x.value == value)?.label}
              />
              <th></th>
            </>
          }
          onRowClick={(item, event) => {
            event.preventDefault();
            setItemToEdit(item);
          }}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {userSettings.permissions.cars.read ? (
                  <CustomLink to={`/admin/cars/${item.car.id}`}>
                    {item.car.currentPlateNumber || item.car.lastPlateNumber}
                  </CustomLink>
                ) : (
                  <>{item.car.currentPlateNumber || item.car.lastPlateNumber}</>
                )}
              </td>
              <td>
                {userSettings.permissions.cars.read ? (
                  <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.vehicleIdentificationNumber}</CustomLink>
                ) : (
                  item.car.vehicleIdentificationNumber
                )}
              </td>
              <td>
                <CustomLink to={`/admin/leasing_companies/${item.leasingCompany?.id}`}>
                  {item.leasingCompany?.name}
                </CustomLink>
              </td>
              <td>{!!item.contractDate && formatDateNumeric(item.contractDate, i18n.language)}</td>
              <td>{!!item.expirationDate && formatDateNumeric(item.expirationDate, i18n.language)}</td>
              <td>
                <State
                  text={states.find((x) => x.value == item.car.status)?.label ?? ""}
                  color={getCarStateColor(item.car.status)}
                />
              </td>
            </>
          )}
          getRowStyle={(item) =>
            isItemExpiringSoon(item.expirationDate, item.car.status)
              ? ({ "--row-bg": "#fffed9", "--row-hover-bg": "#f2f0c0" } as React.CSSProperties)
              : isItemExpired(item.expirationDate, item.car.status)
                ? ({ "--row-bg": "var(--red-20)", "--row-hover-bg": "var(--red-40)" } as React.CSSProperties)
                : undefined
          }
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />

        {!!itemToDelete && (
          <DeleteCarLeasingDialog onClose={() => setItemToDelete(undefined)} carLeasingCarId={itemToDelete.car.id} />
        )}

        {!!showNewItem && <CreateCarLeasingDialog onClose={() => setShowNewItem(false)} />}

        {!!itemToEdit && <EditCarLeasingDialog carId={itemToEdit.car.id} onClose={() => setItemToEdit(undefined)} />}

        {!!itemToConclude && (
          <ConcludeCarLeasingDialog carId={itemToConclude.car.id} onClose={() => setItemToConclude(undefined)} />
        )}
      </Panel>

      {!!itemToShowVersions && (
        <CarLeasingHistoryDialog
          itemToShowVersionsId={itemToShowVersions.car.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );
};

export default CarLeasings;
